import React, { useState, useEffect } from 'react';
import { useParams, Link, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { getAMIById, getAMILineage, getAMICountByAccountId } from '../services/api';
import type { AMIResponse, LineageItem, UniqueFile, DBStatusResponse } from '../types/api';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAMIUniqueFileHashes, getAMIEC2Status, getAMIDBStatus } from '../services/api';
import { gunzipSync, strFromU8 } from 'fflate';
import SearchIcon from '@mui/icons-material/Search';
import CompareDiffAccordion from '../components/CompareDiffAccordion';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Not available';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  } catch {
    return 'Invalid date';
  }
};

const AMIDetails = () => {
  const { imageId } = useParams<{ imageId: string }>();
  const [ami, setAMI] = useState<AMIResponse | null>(null);
  const [lineage, setLineage] = useState<LineageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // New: Store details for each lineage item
  const [lineageDetails, setLineageDetails] = useState<Record<string, AMIResponse | null>>({});
  const [lineageLoading, setLineageLoading] = useState(false);
  const [accountAmiCount, setAccountAmiCount] = useState<number | null>(null);

  // Unique file hashes
  const [uniqueFiles, setUniqueFiles] = useState<UniqueFile[]>([]);
  const [uniqueLoading, setUniqueLoading] = useState<boolean>(false);
  const [uniqueError, setUniqueError] = useState<string | null>(null);
  const [dbStatus, setDbStatus] = useState<DBStatusResponse | null>(null);
  const [filters, setFilters] = useState<string[]>([
    '/var/lib/cloud',
    '/etc/ssh/ssh_host',
    '/var/log/',
    '/etc/hostname',
    '/usr/lib/modules',
    '/usr/lib/python',

  ]);
  
  // Auto-exclusions - common paths that are usually not interesting
  const addAutoExclusions = () => {
    const autoExclusions = [
      '/tmp/',
      '/var/cache/',
      '/var/lib/apt/',
      '/var/lib/dpkg/',
      '/var/lib/yum/',
      '/proc/',
      '/sys/',
      '/dev/',
      '/run/',
      '/boot/',
      '/etc/ssh/ssh_host_',
      '/lib/modules/',
      '/usr/share/doc/',
      '/usr/share/man/',
      '/usr/lib/modules/',
      '/var/lib/systemd/',
      '/etc/machine-id',
      '/var/lib/dbus/machine-id',
      '/root/.bash_history',
      '/home/',
      '/var/spool/',
      '/var/mail/',
    ];
    
    setFilters(prev => {
      const newFilters = [...prev];
      autoExclusions.forEach(exclusion => {
        if (!newFilters.includes(exclusion)) {
          newFilters.push(exclusion);
        }
      });
      return newFilters;
    });
  };
  const [filterInput, setFilterInput] = useState<string>('');
  const [includeFilters, setIncludeFilters] = useState<string[]>([]);
  const [includeFilterInput, setIncludeFilterInput] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: 'hash' | 'relativePath' | 'modifiedTimestamp'; direction: 'asc' | 'desc' }>({ key: 'relativePath', direction: 'asc' });

  const [lineageStatuses, setLineageStatuses] = useState<Record<string, 'public' | 'private' | 'deleted' | 'unknown'>>({});
  const [lineageStatusesLoading, setLineageStatusesLoading] = useState<Record<string, boolean>>({});
  const [lineageDbStatuses, setLineageDbStatuses] = useState<Record<string, DBStatusResponse>>({});

  // Scan logs state
  const [stdoutLog, setStdoutLog] = useState<string | null>(null);
  const [stderrLog, setStderrLog] = useState<string | null>(null);
  const [stdoutLoading, setStdoutLoading] = useState(false);
  const [stderrLoading, setStderrLoading] = useState(false);
  const [stdoutExists, setStdoutExists] = useState(false);
  const [stderrExists, setStderrExists] = useState(false);

  // Scan submission state
  const [scanSubmitting, setScanSubmitting] = useState(false);
  const [scanSubmitSuccess, setScanSubmitSuccess] = useState(false);
  const [scanSubmitError, setScanSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!imageId) return;
      setLoading(true);
      setError(null);
      try {
        const [amiResponse, lineageResponse] = await Promise.all([
          getAMIById(imageId),
          getAMILineage(imageId),
        ]);

        if (amiResponse.error) {
          setError(amiResponse.error);
        } else if (Array.isArray(amiResponse.data) && amiResponse.data.length > 0) {
          setAMI(amiResponse.data[0]);
        } else {
          setError('AMI not found');
        }

        if (lineageResponse.error) {
          setError(lineageResponse.error);
        } else {
          setLineage(lineageResponse.data);
        }
      } catch (err) {
        setError('Failed to fetch AMI details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [imageId]);

  // Fetch full details for each lineage item
  useEffect(() => {
    const fetchLineageDetails = async () => {
      setLineageLoading(true);
      const details: Record<string, AMIResponse | null> = {};
      await Promise.all(
        lineage.map(async (item) => {
          try {
            const res = await getAMIById(item.imageId);
            if (res.data && Array.isArray(res.data) && res.data.length > 0) {
              details[item.imageId] = res.data[0];
            } else if (res.data && res.data.amiId) {
              details[item.imageId] = res.data;
            } else {
              details[item.imageId] = null;
            }
          } catch {
            details[item.imageId] = null;
          }
        })
      );
      setLineageDetails(details);
      setLineageLoading(false);
    };
    if (lineage.length > 0) {
      fetchLineageDetails();
    } else {
      setLineageDetails({});
    }
  }, [lineage]);

  // Fetch AMI statuses for all lineage items
  useEffect(() => {
    const fetchLineageStatuses = async () => {
      if (lineage.length === 0) return;

      // Process lineage items sequentially to avoid overwhelming the API
      for (const item of lineage) {
        setLineageStatusesLoading((prev) => ({ ...prev, [item.imageId]: true }));
        try {
          // Get region from the lineage item (always available from the lineage API response)
          const region = item.region;

          // Check EC2 status with the region
          const statusResult = await getAMIEC2Status(item.imageId, region);
          setLineageStatuses((prev) => ({ ...prev, [item.imageId]: statusResult.status }));
        } catch (err) {
          console.error(`Failed to fetch status for ${item.imageId}:`, err);
          setLineageStatuses((prev) => ({ ...prev, [item.imageId]: 'unknown' }));
        } finally {
          setLineageStatusesLoading((prev) => ({ ...prev, [item.imageId]: false }));
        }
      }
    };

    // Fetch statuses once lineage is loaded (lineage items contain region info)
    if (lineage.length > 0) {
      fetchLineageStatuses();
    }
  }, [lineage]);

  // Fetch DB scan statuses for all lineage items
  useEffect(() => {
    const fetchLineageDbStatuses = async () => {
      if (lineage.length === 0) return;

      const dbStatuses: Record<string, DBStatusResponse> = {};
      await Promise.all(
        lineage.map(async (item) => {
          try {
            const status = await getAMIDBStatus(item.imageId);
            dbStatuses[item.imageId] = status;
          } catch (err) {
            console.error(`Failed to fetch DB status for ${item.imageId}:`, err);
            dbStatuses[item.imageId] = { exists: false };
          }
        })
      );
      setLineageDbStatuses(dbStatuses);
    };

    if (lineage.length > 0) {
      fetchLineageDbStatuses();
    }
  }, [lineage]);

  useEffect(() => {
    // Fetch account AMI count using count endpoint
    const fetchAccountAmiCount = async () => {
      if (!ami?.ownerId) return;
      try {
        const res = await getAMICountByAccountId(ami.ownerId);
        if (res.data && res.data.count !== undefined) {
          setAccountAmiCount(res.data.count);
        } else {
          setAccountAmiCount(null);
        }
      } catch {
        setAccountAmiCount(null);
      }
    };
    if (ami) {
      fetchAccountAmiCount();
    }
  }, [ami]);

  // Fetch DB status and unique file hashes
  useEffect(() => {
    const fetchUnique = async () => {
      if (!ami?.amiId) return;
      setUniqueLoading(true);
      setUniqueError(null);

      try {
        // First, check the AMI status in the database
        const status = await getAMIDBStatus(ami.amiId);
        setDbStatus(status);

        if (!status.exists) {
          // AMI is not in the hash database
          if (ami.platformDetails && ami.platformDetails.toLowerCase().includes('windows')) {
            setUniqueError('Currently NTFS filesystems are not being scanned. This will be fixed in the near future');
          } else {
            setUniqueError('AMI not found in hash database.');
          }
          setUniqueFiles([]);
          return;
        }

        if (!status.scanned) {
          // AMI exists but has not been scanned
          setUniqueError(null); // Clear error since we'll show scan button instead
          setUniqueFiles([]);
          return;
        }

        // AMI exists and is scanned, now make the expensive unique hashes query
        const res = await getAMIUniqueFileHashes(ami.amiId);
        if (res.error) {
          // AMI exists but the unique hashes query failed (likely timeout)
          setUniqueError('Failed to load unique file hashes. Please try refreshing the page.');
          setUniqueFiles([]);
        } else {
          setUniqueFiles(res.data || []);
        }
      } catch {
        // Handle any unexpected errors
        setUniqueError('Failed to fetch unique file hashes. Please try refreshing the page.');
        setUniqueFiles([]);
      } finally {
        setUniqueLoading(false);
      }
    };
    fetchUnique();
  }, [ami]);

  // Check scan log existence
  useEffect(() => {
    const checkLogExistence = async () => {
      if (!imageId) return;

      // Check both stdout and stderr in parallel
      await Promise.all([
        fetchScanLog('stdout'),
        fetchScanLog('stderr')
      ]);
    };

    checkLogExistence();
  }, [imageId]);

  const sortedFilteredFiles = (() => {
    const filtered = uniqueFiles.filter((file) => {
      // First apply exclude filters (must not match any exclude filter)
      const excludeMatch = filters.some((f) =>
        file.hash.toLowerCase().includes(f.toLowerCase()) ||
        file.relativePath.toLowerCase().includes(f.toLowerCase())
      );
      
      if (excludeMatch) return false;
      
      // Then apply include filters (if any exist, must match at least one include filter)
      if (includeFilters.length > 0) {
        const includeMatch = includeFilters.some((f) =>
          file.hash.toLowerCase().includes(f.toLowerCase()) ||
          file.relativePath.toLowerCase().includes(f.toLowerCase())
        );
        return includeMatch;
      }
      
      return true;
    });
    
    const sorted = [...filtered].sort((a, b) => {
      if (sortConfig.key === 'modifiedTimestamp') {
        const aTime = a.modifiedTimestamp ? new Date(a.modifiedTimestamp).getTime() : 0;
        const bTime = b.modifiedTimestamp ? new Date(b.modifiedTimestamp).getTime() : 0;
        if (aTime < bTime) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aTime > bTime) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }
      const aVal = a[sortConfig.key].toLowerCase();
      const bVal = b[sortConfig.key].toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  })();

  const handleSort = (key: 'hash' | 'relativePath' | 'modifiedTimestamp') => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filterInput.trim()) {
      e.preventDefault();
      setFilters((prev) => [...prev, filterInput.trim()]);
      setFilterInput('');
    }
  };

  const handleRemoveFilter = (value: string) => {
    setFilters((prev) => prev.filter((f) => f !== value));
  };

  const handleIncludeFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && includeFilterInput.trim()) {
      e.preventDefault();
      setIncludeFilters((prev) => [...prev, includeFilterInput.trim()]);
      setIncludeFilterInput('');
    }
  };

  const handleRemoveIncludeFilter = (value: string) => {
    setIncludeFilters((prev) => prev.filter((f) => f !== value));
  };

  // Fetch scan logs and check existence
  const fetchScanLog = async (logType: 'stdout' | 'stderr') => {
    if (!imageId) return;

    const setLoading = logType === 'stdout' ? setStdoutLoading : setStderrLoading;
    const setLog = logType === 'stdout' ? setStdoutLog : setStderrLog;
    const setExists = logType === 'stdout' ? setStdoutExists : setStderrExists;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/database/imageId/${imageId}/scanlogs/${logType}`,
        { validateStatus: (status) => status < 500 } // Don't throw on 404
      );

      if (response.status === 200 && response.data && response.data.trim().length > 0) {
        // Only show if we have actual content (not empty or whitespace-only)
        setLog(response.data);
        setExists(true);
      } else {
        // Empty content or 404 - don't show accordion
        setExists(false);
      }
    } catch (error) {
      console.error(`Error fetching ${logType} log:`, error);
      setExists(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadHashes = async (format: string) => {
    if (!imageId) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/database/imageId/${imageId}/hashes/download`,
        { responseType: 'blob', params: { format } }
      );
      // Read blob as ArrayBuffer
      const arrayBuffer = await response.data.arrayBuffer();
      // Decompress gzip
      const decompressed = gunzipSync(new Uint8Array(arrayBuffer));
      // Convert to string
      const text = strFromU8(decompressed);
      // Create a new blob for download
      const blob = new Blob([text], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hashes_${imageId}_${format.toUpperCase()}.txt`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download hashes', err);
    }
  };

  const handleScanSubmit = async () => {
    if (!imageId) return;
    setScanSubmitting(true);
    setScanSubmitError(null);
    setScanSubmitSuccess(false);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/database/admin/scan?imageId=${imageId}`
      );
      if (response.status === 200 || response.status === 202) {
        setScanSubmitSuccess(true);
        // Refresh dbStatus immediately to show pending state
        if (ami?.amiId) {
          const status = await getAMIDBStatus(ami.amiId);
          setDbStatus(status);
        }
      }
    } catch (err: unknown) {
      console.error('Failed to submit scan', err);
      const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      setScanSubmitError(message || 'Failed to submit AMI for scanning. Please try again.');
    } finally {
      setScanSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!ami) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">AMI not found</Alert>
      </Container>
    );
  }

  const currentAmiStatus = imageId ? lineageStatuses[imageId] : undefined;
  const isCurrentAmiStatusLoading = imageId ? lineageStatusesLoading[imageId] : false;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mb: 4 }}>
        <Navbar />
      </Box>
      <Typography variant="h4" gutterBottom>
        {ami.name}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography
          color="text.secondary"
          gutterBottom
          sx={{ fontFamily: 'monospace', userSelect: 'all', fontSize: '1.1rem', mr: 1 }}
        >
          {ami.amiId}
        </Typography>
        <Tooltip title="Copy AMI ID">
          <IconButton
            size="small"
            onClick={() => navigator.clipboard.writeText(ami.amiId)}
            aria-label="Copy AMI ID"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        {/* 2x2 Top Grid */}
        <Grid item xs={12}>
          <Grid container spacing={3} sx={{ minHeight: 400 }}>
            {/* Left column: Account Owner Details (top), Image Details (bottom) */}
            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Account Owner Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Owner Account
                    </Typography>
                    {ami.ownerId && (
                      <Link to={`/account/${ami.ownerId}`}>{ami.ownerId}</Link>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Account Verification Status
                    </Typography>
                    {ami.ownerId && (
                      ami.imageOwnerAlias ? (
                        <Chip label="AWS Verified Account" size="small" sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22 }} />
                      ) : (
                        <Chip label="Unverified AWS Account" size="small" sx={{ bgcolor: 'grey.400', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22 }} />
                      )
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total AMIs for this Account
                    </Typography>
                    <Typography>{accountAmiCount !== null && accountAmiCount !== undefined ? accountAmiCount : 'Unknown'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Image Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Architecture
                    </Typography>
                    <Typography>{ami.architecture}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Platform
                    </Typography>
                    <Typography>{ami.platformDetails}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Region
                    </Typography>
                    <Typography>{ami.regionFound}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography>{ami.description || 'No description available'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {/* Right column: Timeline (top), Parent Lineage (bottom) */}
            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Timeline{' '}
                  {isCurrentAmiStatusLoading ? (
                    <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />
                  ) : currentAmiStatus === 'public' ? (
                    <Chip label="This AMI is still public" size="small" sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22, ml: 1 }} />
                  ) : currentAmiStatus === 'private' ? (
                    <Chip label="This AMI is no longer public" size="small" sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22, ml: 1 }} />
                  ) : currentAmiStatus === 'deleted' ? (
                    <Chip label="This AMI has been deleted" size="small" sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22, ml: 1 }} />
                  ) : null}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      First Seen
                    </Typography>
                    <Typography>{formatDate(ami.firstSeen)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Last Seen
                    </Typography>
                    <Typography>{formatDate(ami.lastSeen)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Creation Date
                    </Typography>
                    <Typography>{formatDate(ami.creationDate)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Deprecation Time
                    </Typography>
                    <Typography>{formatDate(ami.deprecationTime)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Parent Lineage
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Source Image ID
                    </Typography>
                    <Typography>{ami.sourceImageId ? ami.sourceImageId : 'None'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Source Image Region
                    </Typography>
                    <Typography>{ami.sourceImageRegion ? ami.sourceImageRegion : 'None'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Full Lineage (spans full width) */}
        {lineage.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Full Lineage
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {lineage.map((item, index) => {
                  const isThisAMI = index === 0;
                  const details = lineageDetails[item.imageId];
                  const isInDB = !!details && !isThisAMI;
                  const itemStatus = lineageStatuses[item.imageId];
                  const isItemStatusLoading = lineageStatusesLoading[item.imageId];
                  // Status text (what is currently in the parentheses)
                  let status = '';
                  if (isThisAMI) {
                    status = 'This is the current image.';
                  } else if (!details) {
                    status = 'This parent image is not in our database.';
                  } else {
                    status = '';
                  }
                  return (
                    <>
                      <Paper
                        key={item.imageId}
                        variant="outlined"
                        component={isInDB ? RouterLink : 'div'}
                        to={isInDB ? `/image/${item.imageId}` : undefined}
                        sx={{
                          p: 2,
                          bgcolor: isThisAMI ? 'action.selected' : 'background.default',
                          color: isThisAMI ? 'text.disabled' : 'text.primary',
                          cursor: isInDB ? 'pointer' : 'default',
                          textDecoration: 'none',
                          transition: 'box-shadow 0.2s',
                          '&:hover': isInDB ? { boxShadow: 4, bgcolor: 'action.hover' } : {},
                        }}
                      >
                        {/* Row 1: ami-id (this/parent image) | ami-id | ami name */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                          {isThisAMI ? 'This Image' : `Parent Image`} | {item.imageId} | {details?.name || (lineageLoading ? 'Loading...' : 'Unknown')}
                        </Typography>
                        {/* Row 2: Region | Owner Account | Owner Alias | Status */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Region: {item.region}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>|</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Owner Account: {item.imageOwnerId ? (
                              <RouterLink
                                to={`/account/${item.imageOwnerId}`}
                                style={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                {item.imageOwnerId}
                              </RouterLink>
                            ) : (
                              'Unknown'
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>|</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Owner Alias: {item.imageOwnerAlias || 'Unknown'}
                          </Typography>
                          {/* Account verification status */}
                          {item.imageOwnerId && item.imageOwnerId !== '' && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>|</Typography>
                              {item.imageOwnerAlias ? (
                                <Chip label="AWS Verified Account" size="small" sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22 }} />
                              ) : (
                                <Chip label="Unverified AWS Account" size="small" sx={{ bgcolor: 'grey.400', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22 }} />
                              )}
                            </>
                          )}
                          {/* AMI status pill */}
                          {isItemStatusLoading ? (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>|</Typography>
                              <CircularProgress size={16} sx={{ verticalAlign: 'middle' }} />
                            </>
                          ) : itemStatus && itemStatus !== 'unknown' ? (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>|</Typography>
                              {itemStatus === 'public' ? (
                                <Chip label="This AMI is still public" size="small" sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22 }} />
                              ) : itemStatus === 'private' ? (
                                <Chip label="This AMI is no longer public" size="small" sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22 }} />
                              ) : itemStatus === 'deleted' ? (
                                <Chip label="This AMI has been deleted" size="small" sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 22 }} />
                              ) : null}
                            </>
                          ) : null}
                        </Box>
                        {/* Row 3: Status */}
                        {status && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                            {status}
                          </Typography>
                        )}
                      </Paper>
                      {index < lineage.length - 1 && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span style={{ fontSize: '1.2em', marginRight: 4 }}>↑</span> The image (above) was created from the parent image (below) <span style={{ fontSize: '1.2em', marginLeft: 4 }}>↓</span>
                            </Typography>
                          </Box>
                          {/* Compare accordion - only show if both AMIs are scanned */}
                          {(() => {
                            const childStatus = lineageDbStatuses[item.imageId];
                            const parentStatus = lineageDbStatuses[lineage[index + 1].imageId];
                            const bothScanned = childStatus?.exists && childStatus?.scanned && parentStatus?.exists && parentStatus?.scanned;

                            if (bothScanned) {
                              return <CompareDiffAccordion childId={item.imageId} parentId={lineage[index + 1].imageId} />;
                            } else {
                              return (
                                <Alert severity="info" sx={{ my: 1 }}>
                                  Both AMIs must be scanned before files can be compared. Please check back after the AMIs have been processed.
                                </Alert>
                              );
                            }
                          })()}
                        </>
                      )}
                    </>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Unique File Hashes - only shown if scanned */}
        {dbStatus?.exists && dbStatus?.scanned && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography variant="h6">
                      Unique File Hashes{' '}
                      {!uniqueLoading && !uniqueError && `(${sortedFilteredFiles.length})`}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Of all of the AMIs we have scanned, the following files have only been seen on this AMI (click to view)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                {uniqueLoading ? (
                  <CircularProgress />
                ) : uniqueError ? (
                  <Alert severity="error">{uniqueError}</Alert>
                ) : (
                  <>
                {/* Exclude Filters */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <TextField
                    label="Exclude (press Enter)"
                    variant="outlined"
                    size="small"
                    value={filterInput}
                    onChange={(e) => setFilterInput(e.target.value)}
                    onKeyDown={handleFilterKeyDown}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={addAutoExclusions}
                    sx={{ minWidth: 'auto' }}
                  >
                    + Auto Exclusions
                  </Button>
                  {filters.map((f) => (
                    <Chip
                      key={f}
                      label={f}
                      onDelete={() => handleRemoveFilter(f)}
                      deleteIcon={<DeleteIcon />}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  ))}
                </Box>

                {/* Include Filters */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <TextField
                    label="Show only (press Enter)"
                    variant="outlined"
                    size="small"
                    value={includeFilterInput}
                    onChange={(e) => setIncludeFilterInput(e.target.value)}
                    onKeyDown={handleIncludeFilterKeyDown}
                    helperText="Filter to show only files matching these paths"
                  />
                  {includeFilters.map((f) => (
                    <Chip
                      key={f}
                      label={f}
                      onDelete={() => handleRemoveIncludeFilter(f)}
                      deleteIcon={<DeleteIcon />}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '80px', padding: '6px 8px', fontSize: '0.75rem' }}>
                        <TableSortLabel
                          active={sortConfig.key === 'hash'}
                          direction={sortConfig.key === 'hash' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('hash')}
                        >
                          Actions
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 'auto', padding: '6px 8px', fontSize: '0.75rem' }}>
                        <TableSortLabel
                          active={sortConfig.key === 'relativePath'}
                          direction={sortConfig.key === 'relativePath' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('relativePath')}
                        >
                          Relative Path
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '180px', padding: '6px 8px', fontSize: '0.75rem' }}>
                        <TableSortLabel
                          active={sortConfig.key === 'modifiedTimestamp'}
                          direction={sortConfig.key === 'modifiedTimestamp' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('modifiedTimestamp')}
                        >
                          Modified Date
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedFilteredFiles.map((file) => (
                      <TableRow key={`${file.hash}-${file.relativePath}`}>
                        <TableCell sx={{ width: '80px', padding: '6px 8px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={`Copy hash: ${file.hash}`}>
                              <IconButton
                                size="small"
                                onClick={() => navigator.clipboard.writeText(file.hash)}
                                aria-label="Copy hash"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Lookup on VirusTotal: ${file.hash}`}>
                              <IconButton
                                size="small"
                                component="a"
                                href={`https://www.virustotal.com/gui/file/${file.hash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Lookup on VirusTotal"
                              >
                                <SearchIcon fontSize="small" sx={{ color: '#4B8DF8' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', width: 'auto', padding: '6px 8px', fontSize: '0.75rem' }}>{file.relativePath}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', width: '180px', padding: '6px 8px', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{formatDate(file.modifiedTimestamp)}</TableCell>
                      </TableRow>
                    ))}
                    {sortedFilteredFiles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            No files match current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
        )}

        {/* Download All Hashes - only shown if scanned */}
        {dbStatus?.exists && dbStatus?.scanned && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Download All File Hashes
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Download all file hashes for this AMI in your preferred format.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleDownloadHashes('txt')}
                >
                  Download as TXT
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleDownloadHashes('json')}
                >
                  Download as JSON
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleDownloadHashes('csv')}
                >
                  Download as CSV
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Scan Submission - shown if exists but not scanned */}
        {dbStatus?.exists && !dbStatus?.scanned && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              {dbStatus?.status === 'pending' ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Scan Pending
                  </Typography>
                  {scanSubmitSuccess ? (
                    <Alert severity="success">
                      AMI successfully submitted for scanning! The scan will be processed shortly.
                    </Alert>
                  ) : (
                    <Alert severity="info">
                      This AMI has been submitted for scanning and is waiting to be processed. Check back soon for results.
                    </Alert>
                  )}
                </>
              ) : dbStatus?.status === 'processing' ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Scan in Progress
                  </Typography>
                  <Alert severity="info">
                    This AMI is currently being scanned. This may take several minutes. Check back soon for results.
                  </Alert>
                </>
              ) : dbStatus?.status === 'failed' ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Scan Failed
                  </Typography>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    The scan for this AMI failed. You can try submitting it again below.
                  </Alert>
                  {scanSubmitSuccess ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      AMI successfully resubmitted for scanning!
                    </Alert>
                  ) : (
                    <>
                      {scanSubmitError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {scanSubmitError}
                        </Alert>
                      )}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleScanSubmit}
                        disabled={scanSubmitting}
                      >
                        {scanSubmitting ? 'Submitting...' : 'Retry Scan'}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    AMI Not Yet Scanned
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    This AMI has been discovered but has not been scanned for file hashes yet. You can submit it for scanning below.
                  </Typography>
                  {scanSubmitSuccess ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      AMI successfully submitted for scanning! The scan will be processed shortly.
                    </Alert>
                  ) : (
                    <>
                      {scanSubmitError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {scanSubmitError}
                        </Alert>
                      )}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleScanSubmit}
                        disabled={scanSubmitting}
                      >
                        {scanSubmitting ? 'Submitting...' : 'Submit for Scanning'}
                      </Button>
                    </>
                  )}
                </>
              )}
            </Paper>
          </Grid>
        )}

        {/* Scan Logs Section */}
        {(stdoutExists || stderrExists) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                CloudShovel Scan Logs
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                View the CloudShovel scan output and error logs for this AMI.
              </Typography>

              {/* Standard Output Log */}
              {stdoutExists && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary
                    expandIcon={stdoutLoading ? <CircularProgress size={20} /> : <ExpandMoreIcon />}
                  >
                    <Typography>Standard Output (stdout.log)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {stdoutLog ? (
                      <Box
                        component="pre"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          p: 2,
                          borderRadius: 1,
                          overflow: 'auto',
                          maxHeight: '500px',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {stdoutLog}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">Loading...</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Standard Error Log */}
              {stderrExists && (
                <Accordion>
                  <AccordionSummary
                    expandIcon={stderrLoading ? <CircularProgress size={20} /> : <ExpandMoreIcon />}
                  >
                    <Typography>Standard Error (stderr.log)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {stderrLog ? (
                      <Box
                        component="pre"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          p: 2,
                          borderRadius: 1,
                          overflow: 'auto',
                          maxHeight: '500px',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {stderrLog}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">Loading...</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            </Paper>
          </Grid>
        )}

        {/* File Hashes and File Timestamps (side by side) - temporarily disabled */}
        {/*
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="h6" gutterBottom>
                  Image File Hashes
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  View all file hashes for this Image as plain text.
                </Typography>
                {hashesFileExists === null ? (
                  <CircularProgress size={20} />
                ) : hashesFileExists ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to={`/image/${ami.amiId}/files/hashes`}
                  >
                    View All File Hashes
                  </Button>
                ) : accountAmiCount !== null && accountAmiCount >= 25 ? (
                  <Typography color="text.secondary">
                    This Image's account has 25 or more images, so file hashes are not generated for this Image.
                  </Typography>
                ) : (
                  <Typography color="text.secondary">
                    This Image's filesystem has not been scanned (feature added after this Image was processed).
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="h6" gutterBottom>
                  Image File Timestamps
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  View all file timestamps for this Image as plain text.
                </Typography>
                {timestampsFileExists === null ? (
                  <CircularProgress size={20} />
                ) : timestampsFileExists ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to={`/image/${ami.amiId}/files/timestamps`}
                  >
                    View All File Timestamps
                  </Button>
                ) : accountAmiCount !== null && accountAmiCount >= 25 ? (
                  <Typography color="text.secondary">
                    This Image's account has 25 or more images, so file timestamps are not generated for this Image.
                  </Typography>
                ) : (
                  <Typography color="text.secondary">
                    This Image's filesystem has not been scanned (feature added after this Image was processed).
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        */}
      </Grid>
    </Container>
  );
};

export default AMIDetails; 